/* eslint-disable @typescript-eslint/no-explicit-any */
import arrowLeft from '@/assets/svg/backIcon.svg?react';

import { Box, Text, Heading, Flex, Stack, Icon, HStack } from '@chakra-ui/react';
import CustomInput from '@/components/common/Input';
import CustomButton from '@/components/common/Button';
import LockIcon from '@/assets/svg/lock.svg?react';
import LockIconRed from '@/assets/svg/lock-red.svg?react';
import EyeIcon from '@/assets/svg/eye.svg?react';
import CheckIcon from '@/assets/svg/check-mark-validation.svg?react';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useResetPasswordMutation } from '@/services/forgotPassword/forgotPassword';
import useCustomToast from '@/components/common/Toast';
import OutlineButton from '@/components/common/UtilityButton';
import { useEffect } from 'react';
import AuthWrapper from '@/layouts/AuthWrapper';

interface IFormValues {
  password: string;
  confirm_password: string;
}

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[\W_]/, 'Must contain one special character')
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const { showToast } = useCustomToast();

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<IFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  });

  useEffect(() => {
    if (errors.password || errors.confirm_password) {
      clearErrors(['password', 'confirm_password']);
    }
  }, [watch('password'), watch('confirm_password')]);

  const { mutateAsync: resetPassword, isLoading } = useResetPasswordMutation();

  const password = watch('password');
  const params = new URLSearchParams(location.search);
  const uid = params.get('uid');
  const token = params.get('token');

  const onSubmit = async (data: IFormValues) => {
    try {
      await resetPassword({
        password: data.password,
        confirm_password: data.confirm_password,
        uid: uid ?? '',
        token: token ?? '',
      });

      showToast('success', 'Password changed successfully.');
      navigate(NAVIGATION_ROUTES.RESET_PASSWORD_SUCCESS);
    } catch (error: any) {
      if (error?.response?.status === 500) {
        showToast('error', 'Something went wrong. Please try again later.');
      } else if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.password) {
          showToast(
            'error',
            Array.isArray(errorData.password) ? errorData.password[0] : errorData.password,
          );
        } else if (errorData.confirm_password) {
          showToast(
            'error',
            Array.isArray(errorData.confirm_password)
              ? errorData.confirm_password[0]
              : errorData.confirm_password,
          );
        } else if (errorData.error) {
          showToast('error', errorData.error);
        } else if (errorData.message) {
          showToast('error', errorData.message);
        } else if (errorData.detail) {
          showToast('error', errorData.detail);
        } else if (errorData.non_field_errors) {
          showToast(
            'error',
            Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors[0]
              : errorData.non_field_errors,
          );
        } else {
          showToast('error', 'Failed to reset password. Please try again.');
        }
      } else if (error?.message) {
        showToast('error', error.message);
      } else {
        showToast('error', 'Something went wrong. Please try again later.');
      }
    }
  };

  const isMinLengthValid = password?.length >= 8;
  const hasSpecialChar = /[\W_]/.test(password);

  return (
    <AuthWrapper>
      <Flex
        width={{ base: '100%', sm: '410px' }}
        p={{ base: '16px', lg: 6 }}
        borderRadius="md"
        flexDirection="column"
        justifyContent="center"
        bg="white"
        gap={8}
      >
        <Icon
          as={LockIconRed}
          fill="white"
          boxSize={12}
          p={3}
          borderRadius={'md'}
          bg="primary"
          color={'brand.primary.DEFAULT'}
          border={'1px solid'}
          borderColor={'border.default'}
        />
        <Stack gap={1}>
          <Heading
            as="h2"
            fontWeight="bolder"
            fontSize={{ base: '2xl', lg: '4xl' }}
            fontFamily="primary"
            color="text.primary"
          >
            Set New Password
          </Heading>
          <Text fontSize="sm" color="text.secondary" fontFamily={'primary'}>
            Your new password must be different from previously used passwords.
          </Text>
        </Stack>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={6}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label="New Password"
                  type="password"
                  placeholder="Enter new password"
                  leftIcon={
                    <Icon
                      as={LockIcon}
                      color={errors?.password ? 'status.error' : 'text.secondary'}
                    />
                  }
                  rightIcon={
                    <Icon
                      as={EyeIcon}
                      color={errors?.password ? 'status.error' : 'text.secondary'}
                    />
                  }
                  error={isSubmitted ? errors.password : undefined}
                  onChange={(e) => {
                    field.onChange(e);
                    if (errors.password) clearErrors('password');
                  }}
                />
              )}
            />
            <Stack spacing={'14px'}>
              <Controller
                name="confirm_password"
                control={control}
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Confirm Password"
                    type="password"
                    placeholder="Re-enter new password"
                    leftIcon={
                      <Icon
                        as={LockIcon}
                        color={errors?.confirm_password ? 'status.error' : 'text.secondary'}
                      />
                    }
                    rightIcon={
                      <Icon
                        as={EyeIcon}
                        color={errors?.confirm_password ? 'status.error' : 'text.secondary'}
                      />
                    }
                    error={isSubmitted ? errors.confirm_password : undefined}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.confirm_password) clearErrors('confirm_password');
                    }}
                  />
                )}
              />

              <Stack spacing={1}>
                <HStack>
                  <Box
                    bg={isMinLengthValid ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={2}
                    height={'14px'}
                    width={'14px'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon as={CheckIcon} color="white" fontSize={'8px'} />
                  </Box>
                  <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                    Must be at least 8 characters
                  </Text>
                </HStack>

                <HStack>
                  <Box
                    bg={hasSpecialChar ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={2}
                    height={'14px'}
                    width={'14px'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon as={CheckIcon} color="white" fontSize={'8px'} />
                  </Box>
                  <Text fontSize="xs" color={'text.secondary'} fontFamily={'primary'}>
                    Must contain one special character
                  </Text>
                </HStack>
              </Stack>
            </Stack>

            <CustomButton
              width="100%"
              fontSize="sm"
              fontWeight="semibold"
              fontFamily={'primary'}
              isLoading={isLoading}
              type="submit"
            >
              Reset Password
            </CustomButton>
          </Stack>
        </form>

        <OutlineButton
          alignSelf={'center'}
          variant="noStyle"
          width="fit-content"
          fontSize="14px"
          fontWeight="semibold"
          fontFamily={'primary'}
          border={'none !important'}
          outline={'none'}
          icon={arrowLeft}
          text="Back to Sign in"
          color={'brand.primary.DEFAULT'}
          onClick={() => navigate(NAVIGATION_ROUTES.SIGNIN)}
          _hover={{ color: 'brand.primary.700', textDecoration: 'underline' }}
        ></OutlineButton>
      </Flex>
    </AuthWrapper>
  );
};

export default ResetPassword;
